import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject, from } from 'rxjs';
import { map, switchMap, takeUntil, tap, filter } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import {
  SnAlarm,
  SnCamera,
  SnCameraDisposition,
  SnFloor,
  SnLocation,
  SnTags,
} from '@lib/api/entities';
import { PanoramaAPI, SnCamerasAPI, SnFloorsAPI, SnLocationsAPI } from '@lib/api/services';
import { SnCurrentOrganization, SnCurrentUser } from '@lib/auth';
import { t } from '@lib/translation';
import { FsLiveUpdateSwitchService } from '@lib/transport/live-update-switch';

import {
  LocationMapQueryParams,
  LocationQueryParams,
  LocationsServiceInterface,
} from '../interfaces';

const ROOM_CLEARING_STORE_KEY = 'isRoomClearingEnabled';

@Injectable()
export class LocationsService implements LocationsServiceInterface, OnDestroy {
  private readonly _selectionChange$ = new BehaviorSubject<SnLocation | null>(null);
  private readonly _selectedFloor$ = new BehaviorSubject<SnFloor | null>(null);
  private readonly _cameraSelected$ = new Subject<SnCamera>();
  private readonly _cameraTags$ = new Subject<SnTags>();
  private readonly _removePolyline$ = new Subject<void>();
  private readonly _hovered$ = new Subject<SnCamera | null>();
  private readonly _sdsFloorCameras$ = new Subject<SnCamera[]>();
  private readonly _selectedFloorHasCameras$ = new BehaviorSubject<boolean>(false);
  private readonly _roomsCleared$ = new Subject<void>();
  private readonly _roomsClearedReset$ = new Subject<void>();
  private readonly _destroy$ = new Subject<void>();

  private _selected: SnLocation | null = null;
  private _selectedFloor: SnFloor | null = null;
  private _selectedNumCameras: number = 0;
  private _roomFullyCleared: boolean = false;
  private _floorSubscription: string | null = null;

  public get selectionChange$(): Observable<SnLocation | null> {
    return this._selectionChange$.asObservable();
  }

  public get selectedFloor$(): Observable<SnFloor | null> {
    return this._selectedFloor$.asObservable();
  }

  public get cameraSelected$(): Observable<SnCamera> {
    return this._cameraSelected$.asObservable();
  }

  public get cameraTags$(): Observable<SnTags> {
    return this._cameraTags$.asObservable();
  }

  public get removePolyline$(): Observable<void> {
    return this._removePolyline$.asObservable();
  }

  public get hovered$(): Observable<SnCamera | null> {
    return this._hovered$.asObservable();
  }

  public get sdsFloorCameras$(): Observable<SnCamera[]> {
    return this._sdsFloorCameras$.asObservable();
  }

  public get selectedFloorHasCameras$(): Observable<boolean> {
    return this._selectedFloorHasCameras$.asObservable();
  }

  public get roomsCleared$(): Observable<void> {
    return this._roomsCleared$.asObservable();
  }

  public get roomsClearedReset$(): Observable<void> {
    return this._roomsClearedReset$.asObservable();
  }

  public get selected(): SnLocation | null {
    return this._selected;
  }

  public get selectedFloor(): SnFloor | null {
    return this._selectedFloor;
  }

  public get selectedNumCameras(): number {
    return this._selectedNumCameras;
  }

  public get selectedFloorHasCameras(): boolean {
    return this._selectedFloorHasCameras$.value;
  }

  public get roomFullyCleared(): boolean {
    return this._roomFullyCleared;
  }

  public get isRoomClearingEnabled(): boolean {
    const isRoomClearingEnabledString = localStorage.getItem(ROOM_CLEARING_STORE_KEY);

    if (!this._currentUser.settings?.mapSetting?.isRoomClearingEnabled) {
      if (isRoomClearingEnabledString) {
        return isRoomClearingEnabledString === 'true';
      }
      return false;
    }
    return this._currentUser.settings.mapSetting.isRoomClearingEnabled;
  }

  constructor(
    private readonly _locationsAPI: SnLocationsAPI,
    private readonly _floorsAPI: SnFloorsAPI,
    private readonly _camerasAPI: SnCamerasAPI,
    private readonly _panoramasAPI: PanoramaAPI,
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _currentUser: SnCurrentUser,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _liveUpdateSwitchService: FsLiveUpdateSwitchService,
  ) {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public isSelected(locationId: string): boolean {
    return this._selected?.id === locationId;
  }

  public selectLocation(
    id: string,
    data?: SnAlarm,
    move: boolean = true,
    keepAlarmPopup?: boolean,
  ): Observable<SnLocation> {
    if (this._selected && this._selected.id !== id) {
      this.deselectLocation();
    } else if (this._selected && this._selected.id === id && !data) {
      return new Observable<SnLocation>((observer) => {
        if (this._selected) {
          observer.next(this._selected);
        }
        observer.complete();
      });
    }

    return this._locationsAPI.getLocation(id).pipe(
      tap((location) => {
        this._selected = location;
        this._selectionChange$.next(location);

        if (!data) {
          if (location.defaultFloor) {
            const floor = location.floors.find((floor) => floor.id === location.defaultFloor);
            if (floor) {
              this.setSelectedFloor(floor);
            }
          } else if (location.floors.length > 0 && !location.defaultFloor) {
            this.setSelectedFloor(location.floors[0]);
          }
        }
      }),
      switchMap((location) => {
        if (data) {
          return this.findAndSetFloorMatch(location, data).pipe(map(() => location));
        }
        return new Observable<SnLocation>((observer) => {
          observer.next(location);
          observer.complete();
        });
      }),
      takeUntil(this._destroy$),
    );
  }

  public deselectLocation(): void {
    if (!this._selected) {
      return;
    }

    this._selected = null;
    this._selectionChange$.next(null);
    this._selectedFloor = null;
    this._selectedFloor$.next(null);
    this._roomFullyCleared = false;
    this._selectedFloorHasCameras$.next(false);
  }

  public setSelectedFloor(floor: SnFloor | null): void {
    this._selectedFloor = floor;
    this._selectedFloor$.next(floor);
    this._roomFullyCleared = false;

    if (floor?.clearedBy) {
      this._roomFullyCleared = true;
    }
  }

  public getNearestLocationForSDS(alarm: SnAlarm): Observable<SnLocation | null> {
    const coords = alarm.geometry.coordinates;
    return this._locationsAPI
      .getNearestLocations(coords[0], coords[1], alarm.details['Facility'])
      .pipe(
        map((locations) => {
          if (locations.length && locations[0].distance < 1) {
            return locations[0];
          }
          return null;
        }),
        takeUntil(this._destroy$),
      );
  }

  public fetchLocationData(
    params: LocationQueryParams,
  ): Observable<{ results: SnLocation[]; clusterResults: any[] }> {
    const { bounds, zoom, maxZoom } = params;
    const leftTop = bounds.leftTop;
    const rightBottom = bounds.rightBottom;

    const queryParams: LocationMapQueryParams = {
      pageMapGridSize: 60,
      pageMapClustered: maxZoom !== zoom,
      pageMapZoomLevel: zoom,
      pageMapLeftTop: `${leftTop.lng},${leftTop.lat}`,
      pageMapRightBottom: `${rightBottom.lng},${rightBottom.lat}`,
    };

    return this._locationsAPI.getLocationMap(queryParams).pipe(
      map((response) => ({
        results: response.results,
        clusterResults: response.clusterResults || [],
      })),
      takeUntil(this._destroy$),
    );
  }

  public loadCameraLocation(locationId: string, floorId?: string): Observable<SnCamera[]> {
    return this._camerasAPI
      .getCameraMap({
        location: locationId,
        floor: floorId,
        disposition: SnCameraDisposition.Internal,
        exclude_child_cameras: true,
        pageSize: 'all',
        status__in: 'Up,Down,Unmanaged',
      })
      .pipe(
        map(({ results }) => results),
        tap((cameras) => {
          this._selectedNumCameras = cameras.length;
          this._selectedFloorHasCameras$.next(cameras.length > 0);
        }),
        takeUntil(this._destroy$),
      );
  }

  public loadFloorPanoramas(locationId: string, floorId?: string): Observable<any[]> {
    return this._panoramasAPI
      .getMapPanoramas({
        floor: floorId,
        location: locationId,
        pageSize: 'all',
        disposition: 'INTERNAL',
      })
      .pipe(
        map(({ results }) => results),
        takeUntil(this._destroy$),
      );
  }

  public findAndSetFloorMatch(location: SnLocation, alarm: SnAlarm): Observable<SnFloor | null> {
    const regex = /\d+/;
    const floorMatches: SnFloor[] = [];

    if (alarm.details['FloorId']) {
      const matchingFloor = location.floors.find((floor) => floor.id === alarm.details['FloorId']);
      if (matchingFloor) {
        floorMatches.push(matchingFloor);
      }
    }

    if (floorMatches.length === 0) {
      location.floors.forEach((floor) => {
        const floorName = floor.name.toString();
        const floorNumber = floorName.match(regex);
        if (floorNumber && floorNumber[0] === alarm.details['Floor']) {
          floorMatches.push(floor);
        }
      });
    }

    if (floorMatches.length) {
      this.setSelectedFloor(floorMatches[0]);
      this._camerasAPI
        .getCameraMap({
          location: location.id,
          floor: floorMatches[0].id,
          disposition: SnCameraDisposition.Internal,
          pageSize: 'all',
          status__in: 'Up,Down,Unmanaged',
        })
        .pipe(takeUntil(this._destroy$))
        .subscribe((response) => {
          this._sdsFloorCameras$.next(response.results);
        });
      return new Observable((observer) => {
        observer.next(floorMatches[0]);
        observer.complete();
      });
    } else {
      this._sdsFloorCameras$.next([]);
      return new Observable((observer) => {
        observer.next(null);
        observer.complete();
      });
    }
  }

  public clearAllRooms(locationId?: string, floorId?: string): Observable<void> {
    const selectedId = locationId || this._selected?.id;
    const selectedFloorId = floorId || this._selectedFloor?.id;

    if (!selectedId || !selectedFloorId) {
      return new Observable((observer) => {
        observer.complete();
      });
    }

    return this._httpClient
      .post(`/api/locations/${selectedId}/floors/${selectedFloorId}/clear_complete/`, {})
      .pipe(
        tap({
          next: () => {
            this._toastrService.success(t('locations.floors.success.floorCleared'));
            this._roomFullyCleared = true;
            this._roomsCleared$.next();
          },
          error: () => this._toastrService.error(t('locations.floors.errors.clearFailed')),
        }),
        map(() => undefined),
        takeUntil(this._destroy$),
      );
  }

  public removeClearAllRooms(locationId?: string, floorId?: string): Observable<void> {
    const selectedId = locationId || this._selected?.id;
    const selectedFloorId = floorId || this._selectedFloor?.id;

    if (!selectedId || !selectedFloorId) {
      return new Observable((observer) => {
        observer.complete();
      });
    }

    return this._httpClient
      .post(`/api/locations/${selectedId}/floors/${selectedFloorId}/clear_reset/`, {})
      .pipe(
        tap({
          next: () => {
            this._toastrService.success(t('locations.floors.success.floorCleared'));
            this._roomFullyCleared = false;
            this._roomsClearedReset$.next();
          },
          error: (res: any) => this._toastrService.error(t('locations.floors.errors.clearFailed')),
        }),
        map(() => undefined),
        takeUntil(this._destroy$),
      );
  }

  public clearRoom(
    locationId: string,
    floorId: string,
    coords: { lon: number; lat: number },
  ): Observable<void> {
    const data = {
      lat: coords.lat,
      lon: coords.lon,
    };
    return this._httpClient
      .post(`/api/locations/${locationId}/floors/${floorId}/clear_point/`, data)
      .pipe(
        tap({
          next: () => {
            this._toastrService.success(t('locations.floors.success.roomCleared'));
          },
          error: () => this._toastrService.error(t('locations.floors.errors.clearFailed')),
        }),
        map(() => undefined),
        takeUntil(this._destroy$),
      );
  }

  public removeClearRoom(locationId: string, floorId: string, roomId: string): Observable<void> {
    return this._httpClient
      .delete(`/api/locations/${locationId}/floors/${floorId}/rooms/${roomId}/`)
      .pipe(
        tap({
          next: () => {
            this._toastrService.success(t('locations.floors.success.roomClearRemoved'));
          },
          error: () =>
            this._toastrService.error(t('locations.floors.errors.removeRoomClearFailed')),
        }),
        map(() => undefined),
        takeUntil(this._destroy$),
      );
  }

  public getFloor(locationId: string, floorId: string): Observable<SnFloor> {
    return this._floorsAPI.getFloor(locationId, floorId).pipe(takeUntil(this._destroy$));
  }

  public onCameraClick(cameraId: string): Observable<SnCamera> {
    return this._camerasAPI.getCamera(cameraId).pipe(
      tap((camera) => {
        this._cameraSelected$.next(camera);
        if (camera.isParentCamera) {
          this._handleParentCams(camera);
        }
      }),
      takeUntil(this._destroy$),
    );
  }

  public onCameraRightClick(cameraId: string): Observable<SnCamera> {
    return this._camerasAPI.getCamera(cameraId).pipe(takeUntil(this._destroy$));
  }

  public emitCameraSelected(camera: SnCamera): void {
    this._cameraSelected$.next(camera);
  }

  public emitCameraTags(tags: SnTags): void {
    this._cameraTags$.next(tags);
  }

  public emitRemovePolyline(): void {
    this._removePolyline$.next();
  }

  public emitHovered(camera: SnCamera | null): void {
    this._hovered$.next(camera);
  }

  public setRoomFullyCleared(value: boolean): void {
    this._roomFullyCleared = value;
  }

  public emitSdsFloorCameras(cameras: SnCamera[]): void {
    this._sdsFloorCameras$.next(cameras);
  }

  public initFloorLiveUpdates(): void {
    this._selectedFloor$
      .pipe(
        switchMap((floor) => {
          this._unsubscribeFromFloorChannel();
          if (!floor?.id) {
            return [];
          }
          return from(this._liveUpdateSwitchService.initLiveTracking()).pipe(
            tap(() => {
              this._subscribeToFloorChannel(floor.id);
            }),
            map(() => floor),
          );
        }),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  private _handleParentCams(camera: SnCamera): void {
    this._camerasAPI
      .getDescendantList(camera.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((response) => {
        if (response.results?.length) {
          response.results.forEach((c) => {
            this._cameraSelected$.next(c);
          });
        }
      });
  }

  private _subscribeToFloorChannel(floorId: string): void {
    this._floorSubscription = `${this._currentOrganization.id}:floor:${floorId}`;
    this._liveUpdateSwitchService.subscribeToChannel({
      channel: this._floorSubscription,
      callback: ({ name, data }) => {
        switch (name) {
          case 'update:floor:clearDataReceived':
          case 'update:floor:clearDataDeleted': {
            const selected = this._selected;
            const selectedFloor = this._selectedFloor;
            if (selected && selectedFloor && selectedFloor.id === floorId) {
              this._reloadAndSetFloor(selected.id, selectedFloor.id);
            }
            break;
          }
          case 'update:floor:clearComplete': {
            this._roomFullyCleared = true;
            this._roomsCleared$.next();
            const selected = this._selected;
            const selectedFloor = this._selectedFloor;
            if (selected && selectedFloor && selectedFloor.id === floorId) {
              this._reloadAndSetFloor(selected.id, selectedFloor.id);
            }
            break;
          }
          case 'update:floor:clearReset': {
            this._roomFullyCleared = false;
            this._roomsClearedReset$.next();
            const selected = this._selected;
            const selectedFloor = this._selectedFloor;
            if (selected && selectedFloor && selectedFloor.id === floorId) {
              this._reloadAndSetFloor(selected.id, selectedFloor.id);
            }
            break;
          }
        }
      },
    });
  }

  private _unsubscribeFromFloorChannel(): void {
    if (this._floorSubscription) {
      this._liveUpdateSwitchService.unsubscribeFromChannel(this._floorSubscription);
      this._floorSubscription = null;
    }
  }

  private _reloadAndSetFloor(locationId: string, floorId: string): void {
    this.getFloor(locationId, floorId)
      .pipe(takeUntil(this._destroy$))
      .subscribe((floor) => {
        const existingFloorIndex = this._selected?.floors?.findIndex((f) => f.id === floor.id);
        if (existingFloorIndex !== undefined && existingFloorIndex >= 0 && this._selected) {
          this._selected.floors[existingFloorIndex] = floor;
          this.setSelectedFloor(this._selected.floors[existingFloorIndex]);
        } else {
          this.setSelectedFloor(floor);
        }
      });
  }
}
